import { FMButton } from '@own/fds-react';
import { useOSBContentFragment } from '../../../../../hooks/owners-osb/aem-content-util/use-osb-content-fragment';
import { VehicleInfo } from '../../../../../models/osb-model/osb-vehicle-lookup';
import './ford-account-vehicle.scss';
import ProfileService from '../../../../../services/profile-service/profile-service';
import { useHttp, useViewport, useOSBStep } from '../../../../../hooks/owners-osb';
import { ReactComponent as DefaultCarImage } from '../../../../../../src/assets/osb-v3/default-vehicle.svg';
import { useHistory } from 'react-router-dom';
import { buildNavigationUrl } from '../../../owners-osb/osb-utils';
import { OsbPathServiceStep } from '../../osb-view-controller';

interface Props {
    vehicleInfo: VehicleInfo;
    vehicleImageUrl: string;
}

const FordAccountVehicle = (props: Props) => {
    const { vehicleInfo, vehicleImageUrl } = props;
    const profileService = new ProfileService();
    const { dispatch } = useHttp();
    const { isMobileView } = useViewport();
    const vehicleStepContent =
        useOSBContentFragment('common/osb-v3', 'vehicle-step') || {};
    const osbContentData =
        useOSBContentFragment('common/osb-v3', 'vehicle-step-extension') || {};
    const history = useHistory();
    const { osbStep } = useOSBStep();

    const addVehicleToFordAccount = async (vehicleInfo: VehicleInfo) => {
        if (vehicleInfo?.vin) {
            dispatch({ type: 'REQUEST' });
            const nickname: string =
                vehicleInfo?.modelName +
                ' ' +
                Number.parseInt(vehicleInfo?.buildDate?.substring(0, 4));
            await profileService
                .addVehicle(vehicleInfo?.vin, nickname)
                .then(result => {
                    if (result.status !== 200 || result.error) {
                        console.log('vehicle is not saved');
                    } else if (result.status === 200) {
                        console.log('vehicle saved successfully');
                    }
                    dispatch({ type: 'RESPONSE' });
                })
                .catch((error: any) => {
                    dispatch({ type: 'ERROR', error: error.message });
                });
                history.push(buildNavigationUrl(OsbPathServiceStep(), osbStep.UrlQueryParams));
        }
    };

    const handleSkipAddVehicle = () => {
        history.push(buildNavigationUrl(OsbPathServiceStep(), osbStep.UrlQueryParams));
    };

    const imageMarkup = (!vehicleImageUrl || vehicleImageUrl === '#') ? (
        <DefaultCarImage
            data-testid="default-car-image"
            className="osb-v3-vehicle-info-skeleton-default-car-image"
        />
    ) : (
        <img
            src={vehicleImageUrl}
            alt="Vehicle"
            crossOrigin="anonymous"
            onError={(e) => {
                (e.currentTarget.style.display = 'none');
                const container = e.currentTarget.parentElement;
                if (container) {
                    const fallback = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                    container.appendChild(fallback);
                }
            }}
        />
    );

    return (
        <div
            className="osb-v3-vehicle-detail-info-container"
            data-testid="osb-v3-vehicle-detail-info-container"
        >
            <div className="osb-v3-vehicle-detail-info-section">
                <div className="osb-v3-vehicle-model-and-year">
                    {vehicleInfo.modelName} {vehicleInfo.buildDate}
                </div>
                <div
                    className="osb-v3-vehicle-detail-desc"
                    data-testid="vehicle-detail-desc"
                >
                    {vehicleStepContent?.addAVehicleDescriptionLabel}
                </div>
                <div className="osb-v3-vehicle-detail-info-other-section">
                    {isMobileView && (
                        <div className="osb-v3-vehicle-detail-info-image">
                            {imageMarkup}
                        </div>
                    )}
                    <div className="osb-v3-vehicle-detail-info-content">
                        {vehicleInfo.registrationNumber && (
                            <div className="osb-v3-vehicle-detail-info-item">
                                <div className="osb-v3-registration-number-label">
                                    {osbContentData?.vehicleRegistrationNumberLabel}
                                </div>
                                <div className="osb-v3-registration-number-value">
                                    {vehicleInfo.registrationNumber}
                                </div>
                            </div>
                        )}
                        {vehicleInfo.vin && (
                            <div className="osb-v3-vehicle-detail-info-item">
                                <div className="osb-v3-vin-label">
                                    {osbContentData?.vehicleIdentificationNumberLabel}
                                </div>
                                <div className="osb-v3-vin-value">{vehicleInfo.vin}</div>
                            </div>
                        )}
                        {vehicleInfo.vin && (
                            <div className="osb-v3-vehicle-detail-info-item">
                                <div className="osb-v3-mileage-label">
                                    {osbContentData?.currentMileageLabel}
                                </div>
                                <div className="osb-v3-mileage-value">
                                    {vehicleInfo.mileageInKm}
                                </div>
                            </div>
                        )}
                    </div>
                    {!isMobileView && (
                        <div className="osb-v3-vehicle-detail-info-image">
                            {imageMarkup}
                        </div>
                    )}
                </div>
            </div>
            <div
                className="osb-v3-vehicle-detail-cta-section"
                data-testid="cta-section"
            >
                <div
                    className="osb-v3-vehicle-detail-save-button"
                    data-testid="save-button"
                >
                    <FMButton
                        className="save-button"
                        href=""
                        label={vehicleStepContent?.saveButtonLabel}
                        theme="light"
                        type="primary"
                        onClick={() => addVehicleToFordAccount(vehicleInfo)}
                    />
                </div>
                <div
                    className="osb-v3-vehicle-detail-no-thanks-button"
                    data-testid="no-thanks-button"
                >
                    <FMButton
                        className="no-thanks-button"
                        href=""
                        label={vehicleStepContent?.noThanksButtonLabel}
                        theme="light"
                        type="secondary"
                        onClick={handleSkipAddVehicle}
                    />
                </div>
            </div>
        </div>
    );
};

export default FordAccountVehicle;
